/* eslint-disable react/no-array-index-key */
// TesseramentoDetails.js

import React, { useEffect, useState } from 'react';
import {
  Heading,
  Text,
  Divider,
  Badge,
  SimpleGrid,
  AlertIcon,
  Alert,
  Center,
  Spinner,
  Box,
  VStack,
  Flex,
  Icon,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { FaMoneyBillWave } from 'react-icons/fa';
import PaymentForm from '../PaymentForm';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const FieldCard = ({ label, value, mask = false }) => (
  <Box
    p={3}
    bg="#2f3136"
    borderRadius="6px"
    border="1px solid"
    borderColor="gray.700"
  >
    <Text
      fontSize="xs"
      color="gray.400"
      fontWeight="600"
      mb={1}
      textTransform="uppercase"
      letterSpacing="0.5px"
    >
      {label}
    </Text>
    <Text
      color="white"
      fontWeight="500"
      data-clarity-mask={mask ? 'True' : undefined}
    >
      {value || '-'}
    </Text>
  </Box>
);

const StatusFieldCard = ({ label, value, status }) => (
  <Box
    p={3}
    bg="#2f3136"
    borderRadius="6px"
    border="1px solid"
    borderColor={status ? '#3eb297' : 'gray.700'}
  >
    <Text
      fontSize="xs"
      color="gray.400"
      fontWeight="600"
      mb={1}
      textTransform="uppercase"
      letterSpacing="0.5px"
    >
      {label}
    </Text>
    <Badge
      bg={status ? '#3eb297' : '#e73341'}
      color="white"
      borderRadius="4px"
      px={2}
      py={1}
      fontSize="xs"
      fontWeight="600"
    >
      {value}
    </Badge>
  </Box>
);

const SectionTitle = ({ children, icon }) => (
  <Heading
    size="sm"
    color="gray.300"
    fontWeight="600"
    textTransform="uppercase"
    letterSpacing="0.5px"
    display="flex"
    alignItems="center"
    mb={3}
  >
    {icon && <Icon as={icon} color="#3eb297" mr={2} />}
    {children}
  </Heading>
);

const TesseramentoDetails = ({
  socio,
  onPaymentSuccess,
  onModalClose,
  remotePaymentStatus,
  autoOpenDrawer = true,
}) => {
  const { userData, requestUserData } = useAuth();
  const [paymentError, setPaymentError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(
    remotePaymentStatus === 'succeeded'
  );
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const [clientPaymentIntentId, setClientPaymentIntentId] = useState('');

  const paymentSucceeded = ['succeeded', 'processing'].includes(
    socio.paymentStatus
  );

  const localOnModalClose = () => {
    setShowPaymentForm(false);
    onModalClose();
  };

  const createOrder = async ({
    paymentIntentId,
    paymentStatus = 'pending',
    paymentMethod = 'contanti',
    paymentMethodId = '',
  } = {}) => {
    try {
      const response = await axios.post('/api/ordini', {
        participantId: socio.documentId,
        paymentIntentId,
        paymentStatus,
        paymentMethod,
        paymentMethodId,
        amount: 10,
        paymentIntents: clientPaymentIntentId,
        eventVariants: {
          [socio.documentId]: {
            type: 'tesseramento',
            priceOption: 'totale',
            amount: 10,
            name: `Tesseramento ${socio.nomeSocio}`,
          },
        },
      });

      if (response.status === 200) {
        onPaymentSuccess();
        setPaymentSuccess(true);
        requestUserData({ withLoading: false });
      } else {
        setPaymentError(`Errore con la creazione dell'ordine`);
      }
    } catch (e) {
      setPaymentError(e.response?.data?.error || e.message);
    }
  };

  const createPaymentIntent = async () => {
    setLoadingSecret(true);

    try {
      const amount = 1000; // convert to cents for Stripe

      const response = await axios.post('/api/create-payment-intent', {
        amount,
        currency: 'eur',
        socioId: socio.documentId,
        stripeCustomerId: userData.stripeCustomerId,
        eventVariants: {
          [socio.documentId]: {
            name: `Tesseramento ${socio.nomeSocio}`,
            type: 'tesseramento',
            priceOption: 'totale',
            amount: 10,
          },
        },
      });

      setClientSecret(response.data.clientSecret);
      setClientPaymentIntentId(response.data.localPaymentIntentId);
      setPaymentError(null);
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setLoadingSecret(false);
    }
  };

  useEffect(() => {
    if (!paymentSucceeded && !paymentSuccess) {
      createPaymentIntent();
    }
  }, [paymentSucceeded, paymentSuccess]);

  useEffect(() => {
    if (!paymentSucceeded && autoOpenDrawer) {
      setShowPaymentForm(true);
    }
  }, [paymentSucceeded, socio, autoOpenDrawer]);

  if (!socio) return null;

  const personalFields = [
    { label: 'Email', value: socio.email },
    { label: 'Nome', value: socio.nomeSocio, mask: true },
    { label: 'Cognome', value: socio.cognomeSocio, mask: true },
    { label: 'Data di Nascita', value: socio.dataNascitaSocio },
    { label: 'Luogo di Nascita', value: socio.luogoNascitaSocio },
    { label: 'Codice Fiscale', value: socio.codiceFiscaleSocio, mask: true },
    {
      label: 'Indirizzo',
      value: `${socio.indirizzoVia} ${socio.indirizzoCitta} ${socio.indirizzoCap}`,
    },
    { label: 'Telefono', value: socio.telefono },
    { label: 'Precauzioni Sanitarie', value: socio.precauzioniSanitarie },
    {
      label: 'Come Sei Venuto a Conoscenza',
      value: socio.comeSeiVenutoConoscenza,
    },
  ];

  const consentFields = [
    {
      label: 'Autorizzazione Fotografie',
      value: socio.autorizzazioneFotografie ? 'Sì' : 'No',
      status: socio.autorizzazioneFotografie,
    },
    {
      label: 'Consenso Pubblicazione',
      value: socio.consensoPubblicazione ? 'Sì' : 'No',
      status: socio.consensoPubblicazione,
    },
    {
      label: 'Termini & Condizioni',
      value: socio.terminiCondizioni ? 'Accettati' : 'Non Accettati',
      status: socio.terminiCondizioni,
    },
  ];

  const paymentFields = [
    {
      label: 'Metodo di Pagamento',
      value: socio.paymentMethod || 'Non specificato',
    },
    {
      label: 'Stato del Pagamento',
      value: socio.paymentStatus || 'Non specificato',
    },
    { label: 'Payment Intent ID', value: socio.paymentIntentId || 'N/A' },
  ];

  const parentFields = [
    { label: 'Nome Genitore', value: socio.nomeGenitore, mask: true },
    { label: 'Cognome Genitore', value: socio.cognomeGenitore, mask: true },
    { label: 'Data di Nascita Genitore', value: socio.dataNascitaGenitore },
    { label: 'Luogo di Nascita Genitore', value: socio.luogoNascitaGenitore },
    {
      label: 'Codice Fiscale Genitore',
      value: socio.codiceFiscaleGenitore,
      mask: true,
    },
  ];

  return (
    <>
      <Box
        bg="#36393f"
        p={{ base: 5, md: 6 }}
        borderRadius="10px"
        boxShadow="0 4px 12px rgba(0,0,0,0.3)"
        border="1px solid"
        borderColor="gray.600"
      >
        {/* 🏷️ Titolo */}
        <Flex align="center" justify="space-between" mb={6}>
          <Heading size="lg" color="white" fontWeight="700">
            Dettagli Tesseramento
          </Heading>
          <Badge
            bg={paymentSucceeded ? '#3eb297' : '#ffcc00'}
            color={paymentSucceeded ? 'white' : 'black'}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="xs"
            fontWeight="600"
          >
            {paymentSucceeded ? 'Attivo' : 'In attesa'}
          </Badge>
        </Flex>
        <Divider mb={6} borderColor="gray.600" />
        <VStack spacing={6} align="stretch">
          <Box>
            <SectionTitle>Informazioni Personali</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {personalFields.map((item) => (
                <FieldCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  mask={item.mask}
                />
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.600" />

          <Box>
            <SectionTitle>Interessi</SectionTitle>
            {socio.interessi && socio.interessi.length > 0 ? (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
                {socio.interessi.map((item) => (
                  <Badge
                    key={item}
                    bg="#36a9e1"
                    color="white"
                    borderRadius="6px"
                    px={4}
                    py={2}
                    fontSize="sm"
                    fontWeight="600"
                    textTransform="none"
                  >
                    {item}
                  </Badge>
                ))}
              </SimpleGrid>
            ) : (
              <Text color="gray.500" fontStyle="italic">
                Nessun interesse specificato
              </Text>
            )}
          </Box>

          <Divider borderColor="gray.600" />

          <Box>
            <SectionTitle>Consensi & Autorizzazioni</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {consentFields.map((item) => (
                <StatusFieldCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  status={item.status}
                />
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.600" />

          <Box>
            <SectionTitle icon={FaMoneyBillWave}>
              Informazioni Pagamento
            </SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {paymentFields.map((item) => (
                <FieldCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </SimpleGrid>
          </Box>

          {socio.isMinorenne && (
            <>
              <Divider borderColor="gray.600" />
              <Box>
                <SectionTitle>Informazioni Genitore</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {parentFields.map((item) => (
                    <FieldCard
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      mask={item.mask}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            </>
          )}

          <Divider borderColor="gray.600" />

          <Box>
            <SectionTitle>Iscrizione</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {[
                { label: 'Data', value: socio.data },
                { label: 'Luogo', value: socio.luogo },
              ].map((item) => (
                <FieldCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      </Box>
      {(!paymentSucceeded || paymentSuccess) && showPaymentForm && (
        <Drawer
          isOpen
          size="md"
          placement={window.innerWidth < 788 ? 'bottom' : 'right'}
          onClose={localOnModalClose}
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton top={4} right={4} />
            <DrawerBody>
              <Box p="12px 0" w="100%">
                <Text fontSize="lg" mb={2}>
                  Pagamento della tessera:
                </Text>
                <Divider mb={4} />
                {paymentSuccess ? (
                  <VStack>
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      Pagamento avvenuto con successo 🚀
                    </Alert>
                    <Text mt={3} fontSize="md" color="gray.700">
                      Se lo stato del pagamento della tessera è ancora indicato
                      come <b>In attesa</b>, non ti preoccupare, presto il
                      sistema si aggiornerà automaticamente e vedrai lo stato
                      corretto.
                    </Text>
                  </VStack>
                ) : (
                  <>
                    <Text
                      fontSize="2xl"
                      mb={2}
                      mt={6}
                      color="brand.300"
                      textAlign="right"
                      width="100%"
                    >
                      Totale: <b>€{Number(10).toFixed(2)}</b>
                    </Text>
                    <Divider />
                    {loadingSecret ? (
                      <Center width="100%" minHeight="inherit" py={4}>
                        <Spinner size="lg" />
                      </Center>
                    ) : (
                      <Elements
                        stripe={stripePromise}
                        options={{ clientSecret }}
                      >
                        <PaymentForm
                          ambito="tessera"
                          onSuccess={({
                            status,
                            paymentIntentId,
                            paymentMethodId,
                          }) => {
                            if (['succeeded', 'processing'].includes(status)) {
                              createOrder({
                                status,
                                paymentIntentId,
                                paymentMethodId,
                                paymentMethod: 'elettronico',
                                paymentStatus: status,
                              });
                            }
                          }}
                        />
                      </Elements>
                    )}
                    {Boolean(paymentError) && (
                      <Alert status="error" mb={4}>
                        <AlertIcon />
                        {paymentError}
                      </Alert>
                    )}
                  </>
                )}
                <Divider mt={4} />
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default TesseramentoDetails;
